// =============================================================================
// backendJson — read a JSON feed from the Apps Script /exec, retrying until it
// actually IS JSON.
//
// The /exec, under load, hands back an HTML error/quota page with HTTP 200
// instead of JSON. A single fetch + r.json() then throws and the read silently
// fails. Every read that a write's confirmation depends on (the "did it land?"
// re-read) MUST ride that out, exactly like the checklist read and the admin
// Command Centre's backendFetch (@accd502). Reads are idempotent, so retrying is
// always safe. A PARSED reply is terminal (even an {error} one); only a
// transport/parse failure is retried.
//
// This is the ONE place that logic lives, shared by the checklist read and the
// EOD / sell-out / stock write-confirmation verifies.
// =============================================================================

export const _defaultSleep = (ms) => new Promise((r) => setTimeout(r, ms));

// Returns the parsed JSON, or null if it never got real JSON within `attempts`.
// `fetchImpl` is injectable for tests (defaults to the real fetch).
export async function fetchJsonWithRetry(
  url,
  { attempts = 4, delayMs = 150, sleep = _defaultSleep, fetchImpl = (u) => fetch(u) } = {}
) {
  for (let i = 0; i < attempts; i++) {
    try {
      const r = await fetchImpl(url);
      if (r && r.ok) return await r.json(); // parses -> terminal; throws on HTML -> retry
      // a non-2xx is transient here (flaky /exec) -> fall through and retry
    } catch (e) {
      /* network error or non-JSON body -> retry */
    }
    if (i < attempts - 1) await sleep(delayMs * (i + 1));
  }
  return null; // exhausted — genuinely couldn't get JSON
}

// Confirm a write landed by RE-READING its feed until a goal predicate holds.
// Beyond fetchJsonWithRetry (which absorbs a non-JSON brownout), this also
// absorbs the brief append→visible LAG after a write: a feed can read cleanly
// yet not yet reflect the row that just committed, so a single read can be a
// clean-but-stale false negative. Re-polls a few times with short backoff.
// Returns:
//   true  — a clean read satisfied `reached`, OR the feed was never readable
//           (onUnreadable:true) so a brownout can't false-rollback a write that
//           may have landed (used for NON-idempotent writes where a re-tap would
//           double-write);
//   false — clean read(s) but the goal never held (onUnreadable defaults false,
//           so an idempotent write surfaces a real "didn't save" and a retry is
//           safe).
export async function confirmByReRead(url, reached, opts = {}) {
  const {
    tries = 3, delayMs = 500, sleep = _defaultSleep,
    fetchImpl = (u) => fetch(u), attempts = 3, onUnreadable = false,
  } = opts;
  let sawClean = false;
  for (let i = 0; i < tries; i++) {
    const d = await fetchJsonWithRetry(url, { attempts, fetchImpl, sleep });
    if (d != null) { sawClean = true; if (reached(d)) return true; }
    if (i < tries - 1) await sleep(delayMs * (i + 1));
  }
  return sawClean ? false : onUnreadable;
}
